/**
 * All scheduling UI operates in Colombia timezone, regardless of where the
 * user's browser is physically located. This wrapper centralizes the
 * conversion so a user in Canada, Europe, or Colombia all see and configure
 * times as "hora Colombia".
 *
 * Why: celery beat fires crontabs in America/Bogota timezone. If the frontend
 * sends UTC timestamps derived from the browser's local time, users outside
 * Colombia end up with a schedule offset by the difference between their
 * local timezone and Colombia.
 */

const moment = require("moment-timezone");

const COLOMBIA_TZ = "America/Bogota";

/**
 * Get the current moment in Colombia timezone.
 */
export const nowInColombia = () => moment.tz(COLOMBIA_TZ);

/**
 * Wrap a native Date (or ISO string) as a Colombia-timezone moment.
 * The returned moment's hour/minute/day fields reflect Colombia wall-clock time.
 */
export const toColombia = (value) => moment.tz(value, COLOMBIA_TZ);

/**
 * Build a Colombia-local Date object that, when the user elects "3:00 AM",
 * the .getHours() in Colombia timezone returns 3. The returned object is a
 * plain JS Date whose underlying UTC timestamp corresponds to the correct
 * Colombia wall-clock time.
 *
 * @param {number} year
 * @param {number} month - zero-indexed (0=Jan)
 * @param {number} day
 * @param {number} hour
 * @param {number} minute
 * @returns {Date}
 */
export const colombiaWallClockToDate = (year, month, day, hour, minute) =>
  moment
    .tz(
      { year, month, day, hour, minute, second: 0, millisecond: 0 },
      COLOMBIA_TZ
    )
    .toDate();

/**
 * Take a native Date and return { year, month, day, hour, minute } representing
 * what that instant LOOKS like in Colombia (regardless of browser timezone).
 */
export const extractColombiaFields = (date) => {
  const m = moment.tz(date, COLOMBIA_TZ);
  return {
    year: m.year(),
    month: m.month(), // zero-indexed
    day: m.date(),
    hour: m.hour(),
    minute: m.minute(),
  };
};

/**
 * Format a Date (or ISO string) as "HH:MM" in Colombia time.
 */
export const formatColombiaHour = (value) =>
  moment.tz(value, COLOMBIA_TZ).format("HH:mm");

/**
 * Format a Date (or ISO string) as "MMM D, YYYY" in Colombia time.
 */
export const formatColombiaShortDate = (value) =>
  moment.tz(value, COLOMBIA_TZ).format("MMM D, YYYY");

export const COLOMBIA_TIMEZONE = COLOMBIA_TZ;
